import { create } from 'zustand';
import type { MeUser } from '../lib/api';

export type FeedTab = 'my' | 'public' | 'requests';

export type AppScreen = 'list' | 'detail' | 'wizard' | 'profile' | 'settings' | 'user-profile';

/** When opening project detail: 'my' = from Мои проекты (use GET /projects/{id}), 'public' = from Витрина (use GET /public/projects/{id}). */
export type DetailSource = 'my' | 'public';

export interface AppNotification {
  id: string;
  message: string;
}

/** Called when FAB "+" is clicked to create a new project (opens wizard). Set by App. */
export type CreateProjectHandler = (() => Promise<void>) | null;
/** Called when FAB "+" is clicked in Заявки — открывает форму создания заявки. */
export type CreateRequestHandler = (() => void) | null;

interface AppState {
  currentUser: MeUser | null;
  activeTab: FeedTab;
  creditBalance: number;
  notifications: AppNotification[];
  screen: AppScreen;
  selectedProjectId: string | null;
  /** Set when navigating to detail: 'my' for drafts/own projects, 'public' for showcase. */
  detailSource: DetailSource | null;
  authError: string | null;
  createProject: CreateProjectHandler;
  createRequest: CreateRequestHandler;
  /** После перехода на вкладку «Заявки» открыть форму создания (для кнопки «+» в «Мои»). */
  createRequestOpenOnNextMount: boolean;
  setCurrentUser: (user: MeUser | null) => void;
  setAuthError: (msg: string | null) => void;
  setActiveTab: (tab: FeedTab) => void;
  setCreditBalance: (balance: number) => void;
  addNotification: (message: string) => void;
  removeNotification: (id: string) => void;
  dismissNotification: (id: string) => void;
  setScreen: (screen: AppScreen) => void;
  setSelectedProjectId: (id: string | null) => void;
  setDetailSource: (source: DetailSource | null) => void;
  setCreateProject: (fn: CreateProjectHandler) => void;
  setCreateRequest: (fn: CreateRequestHandler) => void;
  setCreateRequestOpenOnNextMount: (value: boolean) => void;
  viewProfileUserId: number | null;
  setViewProfileUserId: (id: number | null) => void;
  /** @deprecated Use viewProfileUserId */
  viewProfileTelegramId: number | null;
  /** @deprecated Use setViewProfileUserId */
  setViewProfileTelegramId: (id: number | null) => void;
}

export const useAppStore = create<AppState>((set) => ({
  currentUser: null,
  activeTab: 'my',
  creditBalance: 0,
  notifications: [],
  screen: 'list',
  selectedProjectId: null,
  detailSource: null,
  authError: null,
  createProject: null,
  createRequest: null,
  createRequestOpenOnNextMount: false,

  setCurrentUser: (currentUser) => set({ currentUser }),

  setAuthError: (authError) => set({ authError }),

  setActiveTab: (activeTab) => set({ activeTab }),

  setCreditBalance: (creditBalance) => set({ creditBalance }),

  addNotification: (message) =>
    set((state) => ({
      notifications: [
        ...state.notifications,
        { id: `n-${Date.now()}-${Math.random().toString(36).slice(2)}`, message },
      ],
    })),

  removeNotification: (id) =>
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
    })),

  dismissNotification: (id) =>
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
    })),

  setScreen: (screen) => set({ screen }),

  setSelectedProjectId: (selectedProjectId) => set({ selectedProjectId }),

  setDetailSource: (detailSource) => set({ detailSource }),

  setCreateProject: (createProject) => set({ createProject }),

  setCreateRequest: (createRequest) => set({ createRequest }),

  setCreateRequestOpenOnNextMount: (createRequestOpenOnNextMount) => set({ createRequestOpenOnNextMount }),

  viewProfileUserId: null,
  setViewProfileUserId: (viewProfileUserId) => set({ viewProfileUserId }),

  viewProfileTelegramId: null,
  setViewProfileTelegramId: (viewProfileTelegramId) => set({ viewProfileTelegramId }),
}));
